import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../../App';
import { Briefcase, Users, Shield, TrendingUp, Database } from 'lucide-react';

export default function CorporateTab({ corporateStats, onRefresh }) {
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [bulkEmployeeDialogOpen, setBulkEmployeeDialogOpen] = useState(false);
  const [bulkUsersText, setBulkUsersText] = useState('');

  const [companyForm, setCompanyForm] = useState({
    name: '',
    industry: 'Technology',
    size: 'small',
    city: '',
    address: ''
  });

  const handleCreateCompany = async () => {
    if (!companyForm.name) {
      toast.error('Şirket adı zorunludur');
      return;
    }

    try {
      await api.post('/companies', companyForm);
      toast.success('Şirket oluşturuldu');
      setCompanyDialogOpen(false);
      setCompanyForm({ name: '', industry: 'Technology', size: 'small', city: '', address: '' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şirket oluşturulamadı');
    }
  };

  const handleBulkCreateUsers = async (role) => {
    const lines = bulkUsersText.split('\n').filter(l => l.trim());
    if (lines.length === 0) {
      toast.error('En az bir kullanıcı girin');
      return;
    }

    try {
      const users = lines.map(line => {
        const parts = line.split(',').map(p => p.trim());
        return {
          name: parts[0],
          email: parts[1],
          password: parts[2] || 'User123!',
          role: role
        };
      }).filter(u => u.name && u.email);

      await api.post('/users/bulk', { users });
      toast.success(`${users.length} kullanıcı oluşturuldu`);
      setBulkEmployeeDialogOpen(false);
      setBulkUsersText('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcılar oluşturulamadı');
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-6">
        <Card className="shadow-lg border-none bg-gradient-to-br from-indigo-50 to-blue-50">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-indigo-600" />
              Toplam Şirket
            </CardDescription>
            <CardTitle className="text-3xl text-indigo-600">{corporateStats.total_companies || '-'}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="shadow-lg border-none bg-gradient-to-br from-rose-50 to-pink-50">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4 text-rose-600" />
              Toplam Çalışan
            </CardDescription>
            <CardTitle className="text-3xl text-rose-600">{corporateStats.total_employees || '-'}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="shadow-lg border-none bg-gradient-to-br from-teal-50 to-cyan-50">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-teal-600" />
              HR Kullanıcıları
            </CardDescription>
            <CardTitle className="text-3xl text-teal-600">{corporateStats.total_hr || '-'}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="shadow-lg border-none bg-gradient-to-br from-violet-50 to-purple-50">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-violet-600" />
              Yöneticiler
            </CardDescription>
            <CardTitle className="text-3xl text-violet-600">{corporateStats.total_managers || '-'}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Management Card */}
      <Card className="shadow-lg border-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-indigo-600" />
            Kurumsal Platform Yönetimi
          </CardTitle>
          <CardDescription>Şirketleri, departmanları ve çalışanları yönetin</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Action Buttons */}
            <div className="flex gap-4">
              <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-indigo-600 to-purple-600">
                    <Briefcase className="w-4 h-4 mr-2" />
                    Şirket Ekle
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Yeni Şirket Ekle</DialogTitle>
                    <DialogDescription>Şirket bilgilerini girin</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Şirket Adı</Label>
                      <Input
                        value={companyForm.name}
                        onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})}
                        placeholder="Örn: ABC Teknoloji A.Ş."
                      />
                    </div>
                    <div>
                      <Label>Sektör</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2"
                        value={companyForm.industry}
                        onChange={(e) => setCompanyForm({...companyForm, industry: e.target.value})}
                      >
                        <option value="Technology">Teknoloji</option>
                        <option value="Finance">Finans</option>
                        <option value="Healthcare">Sağlık</option>
                        <option value="Education">Eğitim</option>
                        <option value="Retail">Perakende</option>
                        <option value="Manufacturing">İmalat</option>
                        <option value="Other">Diğer</option>
                      </select>
                    </div>
                    <div>
                      <Label>Şirket Büyüklüğü</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2"
                        value={companyForm.size}
                        onChange={(e) => setCompanyForm({...companyForm, size: e.target.value})}
                      >
                        <option value="small">Küçük (1-50 çalışan)</option>
                        <option value="medium">Orta (51-250 çalışan)</option>
                        <option value="large">Büyük (250+ çalışan)</option>
                      </select>
                    </div>
                    <div>
                      <Label>Şehir</Label>
                      <Input
                        value={companyForm.city}
                        onChange={(e) => setCompanyForm({...companyForm, city: e.target.value})}
                        placeholder="Örn: İstanbul"
                      />
                    </div>
                    <div>
                      <Label>Adres</Label>
                      <Textarea
                        value={companyForm.address}
                        onChange={(e) => setCompanyForm({...companyForm, address: e.target.value})}
                        placeholder="Tam adres"
                      />
                    </div>
                    <Button onClick={handleCreateCompany} className="w-full">
                      Şirket Oluştur
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={bulkEmployeeDialogOpen} onOpenChange={setBulkEmployeeDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Users className="w-4 h-4 mr-2" />
                    Toplu Çalışan Ekle
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Toplu Çalışan Ekle</DialogTitle>
                    <DialogDescription>
                      Her satıra bir çalışan ekleyin. Format: Ad Soyad, Email, Şifre (opsiyonel)
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Çalışan Listesi</Label>
                      <Textarea
                        value={bulkUsersText}
                        onChange={(e) => setBulkUsersText(e.target.value)}
                        placeholder="Ali Yılmaz, ali@sirket.com, Sifre123&#10;Ayşe Demir, ayse@sirket.com&#10;Mehmet Kaya, mehmet@sirket.com"
                        rows={10}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-slate-500 mt-2">
                        Örnek: &quot;Ali Veli, ali@sirket.com, Sifre123&quot; (Şifre belirtilmezse varsayılan: User123!)
                      </p>
                    </div>
                    <Button onClick={() => handleBulkCreateUsers('employee')} className="w-full">
                      <Users className="w-4 h-4 mr-2" />
                      {bulkUsersText.split('\n').filter(l => l.trim()).length} Çalışan Oluştur
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Button variant="outline" onClick={() => toast.info('HR ekleme yakında aktif olacak')}>
                <Shield className="w-4 h-4 mr-2" />
                HR Ekle
              </Button>
            </div>
            
            <div className="bg-slate-50 p-8 rounded-lg text-center">
              <Database className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 mb-2">Şirketler ve çalışanlar burada görünecek</p>
              <p className="text-sm text-slate-400">
                Sisteme kayıtlı kurumsal müşteriler, departmanlar ve çalışanları buradan yönetebilirsiniz.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
